/**
 * Ficha do equipamento de incêndio (#363): a situação no cabeçalho, os vencimentos (uma linha
 * por data que o tipo usa), o histórico de inspeções com corrigir e apagar, e as ações do
 * equipamento. A exclusão mora aqui, não na lista (D9): com inspeções ela é bloqueada e a
 * tela oferece "marcar como fora de uso".
 *
 * Alvo visual: docs/superpowers/specs/2026-10-15-363-incendio-nr23-mockup.html (aba 3).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';
import {
  EventAvailable as DuesIcon,
  History as HistoryIcon,
  LocalFireDepartment as FireIcon,
} from '@mui/icons-material';

import { IdentityBanner, PageShell, RouteActionButton, SectionCard } from '../../components';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { DeleteWithDependenciesDialog } from '../../components/common/DeleteWithDependenciesDialog';
import { ValidityChip } from '../../components/common/ValidityChip';
import { pode } from '../../authorization/pode';
import { useDeleteWithDependencies } from '../../hooks/useDeleteWithDependencies';
import { fireEquipmentService } from '../../services/fireEquipmentService';
import { ApiRequestError } from '../../types/common';
import type { DependencyCheck } from '../../types/dependencyCheck';
import type {
  FireEquipment,
  FireEquipmentDetail,
  FireEquipmentTypes,
  FireInspection,
} from '../../types/fireEquipment';
import { DuesTable } from './components/DuesTable';
import { EquipmentStatusActions } from './components/EquipmentStatusActions';
import { InspectionHistory } from './components/InspectionHistory';
import InspectionDialog from './components/InspectionDialog';
import { equipmentSummary } from './equipmentText';
import { formatIsoDate } from './fireDates';

const TOUCH_TARGET = 44;

const errorMessage = (err: unknown, fallback: string): string => (err instanceof Error ? err.message : fallback);

/** A situação no cabeçalho: o código único e, ao lado, a pendência que ele esconderia. */
const SituationChips: React.FC<{ equipment: FireEquipment; tipos: FireEquipmentTypes }> = ({ equipment, tipos }) => {
  const { code, motivos } = equipment.situation;
  const comingUp = motivos.find((motive) => motive.reason === 'A_VENCER');
  const hiddenPendency = code !== 'COM_PENDENCIA' && motivos.some((motive) => motive.reason === 'COM_PENDENCIA');

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {code === 'A_VENCER' && comingUp?.days !== undefined ? (
        <ValidityChip situation={code} days={comingUp.days} />
      ) : (
        <ValidityChip situation={code} label={tipos.labels.situation[code]} />
      )}
      {hiddenPendency && <ValidityChip situation="COM_PENDENCIA" label={tipos.labels.reason.COM_PENDENCIA} />}
    </Box>
  );
};

/** A quantidade de inspeções que bloqueia a exclusão, como o servidor a conta em `details`. */
function inspectionCount(dependencies: DependencyCheck | null): number {
  const details: Record<string, unknown> = dependencies?.details ?? {};
  return Number(details.inspections ?? 0);
}

export const FireEquipmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [tipos, setTipos] = useState<FireEquipmentTypes | null>(null);
  const [equipment, setEquipment] = useState<FireEquipmentDetail | null>(null);
  const [inspections, setInspections] = useState<FireInspection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [correcting, setCorrecting] = useState<FireInspection | null>(null);
  // Fica até o diálogo terminar de fechar, para o título não piscar "Registrar inspeção".
  const [dialogInspection, setDialogInspection] = useState<FireInspection | undefined>(undefined);
  const [deletingInspection, setDeletingInspection] = useState<FireInspection | null>(null);
  const [deleteInspectionError, setDeleteInspectionError] = useState<string | null>(null);
  const [reactivated, setReactivated] = useState(false);
  const [confirmingDecommission, setConfirmingDecommission] = useState(false);

  // A ficha que está na URL agora: a resposta de outra, que chega atrasada, é descartada.
  const currentId = useRef(id);
  useEffect(() => {
    currentId.current = id;
  }, [id]);

  const load = useCallback(async () => {
    if (!id) return;
    const [types, loaded, history] = await Promise.all([
      fireEquipmentService.getTypes(),
      fireEquipmentService.getFireEquipmentById(id),
      fireEquipmentService.getInspections(id),
    ]);
    if (currentId.current !== id) return;
    setTipos(types);
    setEquipment(loaded);
    setInspections(history);
  }, [id]);

  useEffect(() => {
    let alive = true;
    setError(null);
    setNotFound(false);
    setTipos(null);
    load().catch((err: unknown) => {
      if (!alive) return;
      if (err instanceof ApiRequestError && err.status === 404) setNotFound(true);
      else setError(errorMessage(err, 'Erro ao carregar o equipamento'));
    });
    return () => {
      alive = false;
    };
  }, [load, attempt]);

  /** Recarrega depois de uma ação; a falha entra no aviso da tela, não derruba a ficha. */
  const reload = async () => {
    try {
      await load();
    } catch (err: unknown) {
      if (currentId.current !== id) return;
      setActionError(errorMessage(err, 'Erro ao atualizar a ficha'));
    }
  };

  const changeStatus = async (status: 'IN_USE' | 'DECOMMISSIONED') => {
    if (!id || !equipment) return;
    setBusy(true);
    setActionError(null);
    try {
      await fireEquipmentService.updateFireEquipment(id, { status });
      if (status === 'IN_USE') {
        setReactivated(true);
        enqueueSnackbar(`${equipment.identifier} voltou a ser usado. Confira as validades.`, { variant: 'success' });
      } else {
        setReactivated(false);
        enqueueSnackbar(`${equipment.identifier} marcado como fora de uso.`, { variant: 'success' });
      }
      await reload();
    } catch (err: unknown) {
      setActionError(errorMessage(err, 'Não foi possível mudar a situação do equipamento'));
    } finally {
      setBusy(false);
    }
  };

  const { deleteTarget, deleteDependencies, handleDeleteClick, closeDeleteDialog, handleDeleteConfirm } =
    useDeleteWithDependencies<FireEquipmentDetail, DependencyCheck>({
      checkDependencies: (equipmentId) => fireEquipmentService.checkDependencies(equipmentId),
      deleteEntity: (equipmentId) => fireEquipmentService.deleteFireEquipment(equipmentId),
      checkErrorMessage: 'Erro ao verificar as inspeções do equipamento',
      deleteErrorMessage: 'Erro ao excluir o equipamento',
      onError: setActionError,
      onDeleted: () => {
        enqueueSnackbar('Equipamento excluído.', { variant: 'success' });
        navigate('/fire-equipments');
      },
    });

  const handleInspectionSaved = (saved: FireInspection) => {
    const wasCorrection = correcting !== null;
    setRegistering(false);
    setCorrecting(null);
    enqueueSnackbar(
      wasCorrection
        ? `Inspeção de ${formatIsoDate(saved.inspectionDate)} corrigida.`
        : `Inspeção de ${formatIsoDate(saved.inspectionDate)} registrada.`,
      { variant: 'success' }
    );
    void reload();
  };

  const confirmDeleteInspection = async () => {
    if (!id || !deletingInspection) return;
    setBusy(true);
    setDeleteInspectionError(null);
    try {
      await fireEquipmentService.deleteInspection(id, deletingInspection.id);
      setDeletingInspection(null);
      enqueueSnackbar('Inspeção apagada.', { variant: 'success' });
      await reload();
    } catch (err: unknown) {
      setDeleteInspectionError(errorMessage(err, 'Não foi possível apagar a inspeção'));
    } finally {
      setBusy(false);
    }
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Equipamentos de incêndio', path: '/fire-equipments' },
    { label: equipment?.identifier ?? 'Equipamento' },
  ];
  const backButton = (
    <RouteActionButton
      para="/fire-equipments"
      exige={pode('fire_equipment.read')}
      variant="outlined"
      size="small"
      sx={{ minHeight: TOUCH_TARGET }}
    >
      Voltar
    </RouteActionButton>
  );

  if (notFound) {
    return (
      <PageShell breadcrumbs={breadcrumbs} actions={backButton}>
        <Alert severity="info">
          Não encontramos este equipamento. Ele pode ter sido excluído ou ser de outra empresa.
        </Alert>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell breadcrumbs={breadcrumbs} actions={backButton}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => setAttempt((count) => count + 1)} sx={{ minHeight: TOUCH_TARGET }}>
              Tentar de novo
            </Button>
          }
        >
          {error}
        </Alert>
      </PageShell>
    );
  }

  if (!tipos || !equipment) {
    return (
      <PageShell breadcrumbs={breadcrumbs} actions={backButton}>
        <Skeleton variant="rounded" height={96} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={180} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={220} />
      </PageShell>
    );
  }

  const decommissioned = equipment.status === 'DECOMMISSIONED';
  const summary = equipmentSummary(equipment, tipos);
  const context = [equipment.location, equipment.departmentName, equipment.companyName].filter(Boolean).join(' · ');
  const blockedByInspections = Boolean(deleteDependencies?.hasDependencies);

  return (
    <PageShell breadcrumbs={breadcrumbs} actions={backButton}>
      <IdentityBanner
        avatar={<FireIcon />}
        name={`${equipment.identifier} · ${summary}`}
        status={<SituationChips equipment={equipment} tipos={tipos} />}
        context={context}
      />

      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {reactivated && !decommissioned && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setReactivated(false)}>
          Equipamento reativado. Confira as validades: a próxima inspeção passa a ser hoje.
        </Alert>
      )}

      <EquipmentStatusActions
        equipmentId={equipment.id}
        decommissioned={decommissioned}
        busy={busy}
        onRegisterInspection={() => {
          setDialogInspection(undefined);
          setRegistering(true);
        }}
        onToggleDecommission={() => {
          if (decommissioned) void changeStatus('IN_USE');
          else setConfirmingDecommission(true);
        }}
        onDelete={() => void handleDeleteClick(equipment)}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <SectionCard title="Vencimentos" icon={<DuesIcon />}>
          <DuesTable equipment={equipment} labels={tipos.labels} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Os avisos de vencimento chegam por e-mail ao contato Administrativo da empresa, a partir de 10 dias antes.
          </Typography>
        </SectionCard>
        <SectionCard title="Histórico de inspeções" icon={<HistoryIcon />}>
            <InspectionHistory
              inspections={inspections}
              labels={tipos.labels}
              locked={decommissioned}
              onCorrect={(inspection) => {
                setDialogInspection(inspection);
                setCorrecting(inspection);
              }}
              onDelete={(inspection) => {
                setDeleteInspectionError(null);
                setDeletingInspection(inspection);
              }}
            />
        </SectionCard>
      </Box>

      <InspectionDialog
        open={registering || correcting !== null}
        equipment={{
          id: equipment.id,
          identifier: equipment.identifier,
          location: equipment.location,
          summary,
          inspectionFrequency: equipment.inspectionFrequency,
          lastInspectionDate: equipment.lastInspectionDate,
          inspectionDueFrom: equipment.inspectionDueFrom,
        }}
        labels={tipos.labels}
        inspection={dialogInspection}
        onClose={() => {
          setRegistering(false);
          setCorrecting(null);
        }}
        onSaved={handleInspectionSaved}
      />

      <ConfirmDialog
        open={deletingInspection !== null}
        title="Apagar inspeção"
        message={
          deletingInspection
            ? `Apagar a inspeção de ${formatIsoDate(deletingInspection.inspectionDate)}, feita por ${deletingInspection.performedBy}? O histórico e a próxima inspeção serão recalculados.`
            : ''
        }
        confirmLabel="Apagar inspeção"
        confirmColor="error"
        loading={busy}
        error={deleteInspectionError}
        onConfirm={() => void confirmDeleteInspection()}
        onCancel={() => setDeletingInspection(null)}
      />

      <ConfirmDialog
        open={confirmingDecommission}
        title="Marcar como fora de uso"
        message={`Marcar ${equipment.identifier} como fora de uso? Ele sai do documento e dos avisos de vencimento. Ao reativar, a próxima inspeção volta para hoje.`}
        confirmLabel="Marcar como fora de uso"
        loading={busy}
        onConfirm={() => {
          setConfirmingDecommission(false);
          void changeStatus('DECOMMISSIONED');
        }}
        onCancel={() => setConfirmingDecommission(false)}
      />

      <DeleteWithDependenciesDialog
        open={Boolean(deleteTarget)}
        loading={deleteDependencies === null}
        blocked={blockedByInspections}
        title="Excluir equipamento"
        confirmText="Tem certeza que deseja excluir este equipamento? A exclusão é definitiva: não há lixeira nem como restaurar."
        prerequisite={{
          title: 'Este equipamento tem inspeções',
          reason:
            'O histórico de inspeções é registro de fiscalização e não pode ser apagado junto com o equipamento. Para tirá-lo de circulação sem perder o histórico, marque-o como fora de uso.',
          items: [
            `${inspectionCount(deleteDependencies)} ${inspectionCount(deleteDependencies) === 1 ? 'inspeção registrada' : 'inspeções registradas'}`,
          ],
          resolve: decommissioned
            ? undefined
            : {
                label: 'Marcar como fora de uso',
                permission: 'fire_equipment.update',
                mode: 'modal',
                onResolve: () => {
                  closeDeleteDialog();
                  void changeStatus('DECOMMISSIONED');
                },
              },
          deniedHint: decommissioned
            ? 'Este equipamento já está fora de uso e o histórico dele fica guardado.'
            : 'Peça a um administrador para marcar este equipamento como fora de uso.',
        }}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />
    </PageShell>
  );
};

export default FireEquipmentDetailPage;
